import {Metric} from '../../api/types';

export type RainMetric = 'RR_1H' | 'RR_3H' | 'RR_6H' | 'RR_12H' | 'RR_24H';

/** All supported rain accumulation metrics, ordered from finest to coarsest. */
export const RAIN_METRICS: RainMetric[] = [
  'RR_1H',
  'RR_3H',
  'RR_6H',
  'RR_12H',
  'RR_24H',
];

/** Returns the finest available rain metric for a station, or null. */
export const getStationRainMetric = (measures: Metric[]): RainMetric | null => {
  for (const m of RAIN_METRICS) {
    if (measures.includes(m as Metric)) return m;
  }
  return null;
};

/** Returns true if the station has any rain metric in available_measures. */
export const hasRainData = (measures: Metric[]): boolean =>
  getStationRainMetric(measures) !== null;

/** Returns true if the given metric is a rain accumulation metric. */
export const isRainMetric = (metric: string): metric is RainMetric =>
  RAIN_METRICS.includes(metric as RainMetric);

const NON_ACCUMULATION_RAIN_UNIT_METRICS = ['RR_RATE', 'RR_TODAY'] as const;

/**
 * Returns true if the metric is rendered in the rain unit family (mm ↔ in):
 * the five accumulation periods plus the instantaneous rate and daily-total
 * readings. Use in unit-conversion / coloring / labeling paths that care
 * about the unit family. For cumulative-chart logic, use `isRainMetric`.
 */
export const usesRainUnit = (metric: string): boolean =>
  isRainMetric(metric) ||
  NON_ACCUMULATION_RAIN_UNIT_METRICS.includes(
    metric as (typeof NON_ACCUMULATION_RAIN_UNIT_METRICS)[number],
  );
